import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebaseConnection';
import Header from '../../components/Header';
import PrintModal from '../../components/PrintModal';
import PrintLayout from '../../components/PrintLayout';
import { generatePDF } from '../../utils/pdfGenerator';
import { FiEdit2, FiPlus } from 'react-icons/fi';
import { FaRegTrashAlt } from 'react-icons/fa';
import { GrDocumentTxt } from "react-icons/gr";
import { IoPrint } from "react-icons/io5";
import { AiOutlinePrinter } from 'react-icons/ai';
import { toast } from 'react-toastify';
import { AuthContext } from '../../contexts/auth';

export default function ListaRelatorios() {
  const [relatorios, setRelatorios] = useState([]);
  const [cidades, setCidades] = useState({});
  const [igrejas, setIgrejas] = useState({});
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'ascending' });
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [showCurrentOnly, setShowCurrentOnly] = useState(true);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [selectedRelatorio, setSelectedRelatorio] = useState(null);
  const [igrejasDetalhes, setIgrejasDetalhes] = useState({});
  const [printData, setPrintData] = useState(null);
  const { user } = useContext(AuthContext);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    const loadReports = async () => {
      try {
        const reportsSnapshot = await getDocs(collection(db, 'Reports'));
        const reportsData = reportsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const cidadesSnapshot = await getDocs(collection(db, 'Cidade'));
        const cidadesData = cidadesSnapshot.docs.reduce((acc, doc) => {
          acc[doc.id] = doc.data().nome;
          return acc;
        }, {});

        const igrejasSnapshot = await getDocs(collection(db, 'Igreja'));
        const igrejasData = igrejasSnapshot.docs.reduce((acc, doc) => {
          acc[doc.id] = doc.data().nome;
          return acc;
        }, {});

        const igrejasDetalhesData = igrejasSnapshot.docs.reduce((acc, doc) => {
          const data = doc.data();
          acc[doc.id] = {
            nome: data.nome,
            atriu: data.atriu || 'Átrio',
            corredor: data.corredor || 'Corredor',
            banheiro: data.banheiro || 'Sanitário',
            estacionamento: data.estacionamento || 'Estacionamento',
            som: data.som || 'Som'
          };
          return acc;
        }, {});


        const cargosSnapshot = await getDocs(collection(db, 'Cargo'));
        const cargosData = cargosSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));

        setRelatorios(reportsData);
        setCidades(cidadesData);
        setIgrejas(igrejasData);
        setIgrejasDetalhes(igrejasDetalhesData);
        setCargos(cargosData);
      } catch (error) {
        console.error('Erro ao carregar relatórios:', error);
      }
      setLoading(false);
    };

    loadReports();
  }, []);


  useEffect(() => {
    async function loadPermissions() {
      const storedUser = localStorage.getItem('@userLogado');
      if (!storedUser) {
        console.error('Usuário não encontrado no localStorage.');
        return;
      }

      const userData = JSON.parse(storedUser);
      const cargoId = userData?.cargo;

      if (!cargoId) {
        console.error('ID do cargo não encontrado no usuário.');
        return;
      }

      try {
        const cargoRef = doc(db, 'Cargo', cargoId);
        const cargoDoc = await getDoc(cargoRef);

        if (cargoDoc.exists()) {
          const cargoData = cargoDoc.data();
          setPermissions(cargoData.permissoes || []);
        } else {
          console.error('Documento do cargo não encontrado no Firestore.');
        }
      } catch (error) {
        console.error('Erro ao buscar permissões no Firestore:', error);
      }
    }

    loadPermissions();
  }, []);

  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'Reports', id));
      setRelatorios(relatorios.filter((report) => report.id !== id));
      toast.success('Relatório excluído com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir relatório:', error);
      toast.error('Erro ao excluir relatório. Tente novamente.');
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  

  const sortedReports = React.useMemo(() => {
    let sortableReports = [...relatorios];
    if (sortConfig.key) {
      sortableReports.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableReports;
  }, [relatorios, sortConfig]);

  const filteredReports = sortedReports.filter((report) => {
    const matchesSearch = report.nomeRelatorio.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesActive = !showActiveOnly || report.ativo;
    const matchesCurrent = !showCurrentOnly || report.atual;
    return matchesSearch && matchesActive && matchesCurrent;
  });

  const getReportDates = (report) => {
    if (report.report.length === 0) return { startDate: 'N/A', endDate: 'N/A' };
    const startDate = new Date(report.report[0].date);
    const endDate = new Date(report.report[report.report.length - 1].date);
    return { startDate: startDate.toLocaleDateString(), endDate: endDate.toLocaleDateString() };
  };

  const handleOpenPrintModal = (relatorio) => {
    const relatorioCompleto = {
      ...relatorio,
      cidadeName: cidades[relatorio.cidade],
      igrejaName: igrejas[relatorio.igreja]
    };
    setSelectedRelatorio(relatorioCompleto);
    setIsPrintModalOpen(true);
  };

  const handleGeneratePDF = async (printOption, selectedPerson, startDate, endDate) => {
    let relatoriosParaPDF = [];
    let fileName = 'escala.pdf';

    const filterByDateAndPerson = (relatorio) => {
      const filteredReport = relatorio.report.filter(entry => {
        const entryDate = new Date(entry.date);
        entryDate.setHours(entryDate.getHours() + 3);
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        const isInDateRange = entryDate >= start && entryDate <= end;

        if (!isInDateRange) return false;

        if (printOption === 'individual' && selectedPerson) {
          const locations = ['atriu', 'corredor', 'banheiro', 'estacionamento', 'som'];
          const hasPerson = locations.some(loc => {
            if (entry[loc] && Array.isArray(entry[loc])) {
              return entry[loc].some(person => person && person.name === selectedPerson);
            }
            return false;
          });
          return hasPerson;
        }

        return true;
      });

      if (printOption === 'individual' && selectedPerson) {
        const filteredReportWithPerson = filteredReport.map(entry => {
          const newEntry = { ...entry };
          const locations = ['atriu', 'corredor', 'banheiro', 'estacionamento', 'som'];

          locations.forEach(loc => {
            if (newEntry[loc] && Array.isArray(newEntry[loc])) {
              newEntry[loc] = newEntry[loc].filter(person => person && person.name === selectedPerson);
            }
          });

          return newEntry;
        });

        return {
          ...relatorio,
          report: filteredReportWithPerson
        };
      }

      return {
        ...relatorio,
        report: filteredReport
      };
    };

    if (printOption === 'current') {
      const filtered = filterByDateAndPerson(selectedRelatorio);
      relatoriosParaPDF = [filtered];
      fileName = `${selectedRelatorio.nomeRelatorio.replace(/\s+/g, '_')}.pdf`;
    } else if (printOption === 'individual') {
      const filtered = filterByDateAndPerson(selectedRelatorio);
      relatoriosParaPDF = [filtered];
      fileName = `${selectedPerson.replace(/\s+/g, '_')}_${selectedRelatorio.nomeRelatorio.replace(/\s+/g, '_')}.pdf`;
    } else if (printOption === 'igreja') {
      const igrejasRelatorios = relatorios.filter(
        r => r.igreja === selectedRelatorio.igreja && r.ativo === true
      );
      relatoriosParaPDF = igrejasRelatorios.map(r => filterByDateAndPerson(r));
      fileName = `${selectedRelatorio.igrejaName.replace(/\s+/g, '_')}_escalas.pdf`;
    }

    relatoriosParaPDF = relatoriosParaPDF.filter(r => r.report && r.report.length > 0);

    if (relatoriosParaPDF.length === 0) {
      toast.warning('Nenhum dado encontrado para os filtros selecionados.');
      return;
    }

    setPrintData({
      relatorios: relatoriosParaPDF,
      cidades: cidades,
      igrejas: igrejas,
      igrejasDetalhes: igrejasDetalhes,
      cargos: cargos
    });


    setTimeout(async () => {
      try {
        await generatePDF('pdf-content', fileName);
        toast.success('PDF gerado com sucesso!');
      } catch (error) {
        console.error('Erro ao gerar PDF:', error);
        toast.error('Erro ao gerar PDF. Tente novamente.');
      } finally {
        setPrintData(null);
      }
    }, 500);
  };


  return (
    <div>
      <Header />

      <div className="content">
        <div className="title-container">
          <div className="title-left">
            <GrDocumentTxt size={25} />
            <h1>Relatórios</h1>
          </div>
          <Link to="/relatorio/new" className="title-link incluir">
            <FiPlus color="#FFF" size={25} />
            <span>Gerar relatório</span>
          </Link>
        </div>

        <div className="container dashboard">
          {loading && <span>Carregando relatórios...</span>}
          {!loading && relatorios.length === 0 && <span>Nenhum relatório encontrado...</span>}
        </div>

        <div>
          <input
            className="filter-container"
            type="text"
            placeholder="Buscar por nome..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={showActiveOnly}
                onChange={(e) => setShowActiveOnly(e.target.checked)}
              />
              <span style={{ marginLeft: '10px' }}>Mostrar apenas relatórios ativos</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={showCurrentOnly}
                onChange={(e) => setShowCurrentOnly(e.target.checked)}
              />
              <span style={{ marginLeft: '10px' }}>Mostrar apenas relatórios atual</span>
            </label>
          </div>
        </div>

        {!loading && relatorios.length > 0 && (
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('nomeRelatorio')}>Nome</th>
                <th onClick={() => handleSort('ativo')}>Ativo</th>
                <th onClick={() => handleSort('cidade')}>Cidade</th>
                <th onClick={() => handleSort('igreja')}>Casa de Oração</th>
                <th onClick={() => handleSort('startDate')}>Data de Início</th>
                <th onClick={() => handleSort('endDate')}>Data de Fim</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.map((report, index) => {
                const { startDate, endDate } = getReportDates(report);
                return (
                  <tr className={report.ativo ? 'linha-ativa' : 'linha-inativa'} key={index}>
                    <td data-label="Nome">{report.nomeRelatorio}</td>
                    <td data-label="Ativo">{report.ativo ? 'Ativo' : 'Desativado'}</td>
                    <td data-label="Cidade">{cidades[report.cidade] || 'N/A'}</td>
                    <td data-label="Casa de Oração">{igrejas[report.igreja] || 'N/A'}</td>
                    <td data-label="Data de Início">{startDate}</td>
                    <td data-label="Data de Fim">{endDate}</td>
                    <td data-label="Ação">
                      <Link
                        onClick={() => handleOpenPrintModal(report)}
                        className="action"
                        style={{
                          background: '#65df6bff',
                          border: 'none',
                          cursor: 'pointer',
                          marginRight: '10px'
                        }}
                        title="Imprimir"
                      >
                        <IoPrint color="#FFF" size={17} />
                      </Link>
                      <Link
                        to={`/relatorio/edit/${report.id}`}
                        className="action"
                        style={{ backgroundColor: '#03c9e9' }}
                      >
                        <FiEdit2 color="#FFF" size={17} />
                      </Link>
                      {hasPermission('deletar_itens') && (
                        <button
                          className="action"
                          style={{ backgroundColor: '#e90303' }}
                          onClick={() => handleDelete(report.id)}
                        >
                          <FaRegTrashAlt color="#FFF" size={17} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <PrintModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        relatorio={selectedRelatorio}
        onGeneratePDF={handleGeneratePDF}
      />

      {printData && (
        <PrintLayout
          relatorios={printData.relatorios}
          cidades={printData.cidades}
          igrejas={printData.igrejas}
          igrejasDetalhes={printData.igrejasDetalhes}
          cargos={printData.cargos}
        />
      )}
    </div>
  );
}
